import {
  AuthApiError,
  confirmSmsCode,
  refreshTokens,
  requestSmsCode,
  revokeRefreshToken,
} from './authApi';
import {
  decodeJwt,
  getAccessTokenRaw,
  isExpired,
  readRefreshToken,
  REFRESH_TOKEN_KEY,
  secondsUntilExpiry,
  setAccessToken,
  writeRefreshToken,
} from './tokenStore';

/**
 * Сессия кабинета поверх собственного бэкенда.
 *
 * Источник правды — refresh-токен в localStorage, общий для всех вкладок.
 * Сервер ротирует refresh при каждом обновлении, поэтому обновление сериализуется
 * между вкладками через Web Locks: иначе две вкладки обновятся одним токеном и одна
 * получит 401. Внутри блокировки токен перечитывается — вкладка, дождавшаяся своей
 * очереди, увидит уже обновлённый и второй раз сеть не дёрнет.
 */

export type SessionStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface SessionUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
}

const REFRESH_LOCK = 'unit1.admin.tokenRefresh';
/** За сколько секунд до истечения access обновляем заранее. */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

const SIGNED_OUT: SessionState = { status: 'unauthenticated', user: null };

let state: SessionState = { status: 'loading', user: null };
const listeners = new Set<(next: SessionState) => void>();
let bootstrapPromise: Promise<void> | null = null;
let refreshTimer: number | null = null;
let listenersAttached = false;

function emit(): void {
  for (const listener of listeners) {
    listener(state);
  }
}

function setState(next: SessionState): void {
  const changed = next.status !== state.status || next.user?.uid !== state.user?.uid;
  state = next;
  if (changed) emit();
}

function userFromAccessToken(accessToken: string): SessionUser | null {
  const payload = decodeJwt(accessToken);
  if (!payload) return null;
  return { uid: payload.sub, email: null, displayName: null };
}

function cancelScheduledRefresh(): void {
  if (refreshTimer !== null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/** Обновляем заранее, чтобы сессия не отваливалась на первом же защищённом запросе. */
function scheduleRefresh(accessToken: string): void {
  cancelScheduledRefresh();

  const lead = secondsUntilExpiry(accessToken) - PROACTIVE_REFRESH_LEAD_SECONDS;
  const delayMs = Math.max(lead, 5) * 1000;

  refreshTimer = window.setTimeout(() => {
    void ensureFreshAccessToken().catch(() => {
      // Сеть могла отвалиться — повторим по возвращении фокуса или соединения.
    });
  }, delayMs);
}

function applyTokens(accessToken: string, refreshToken: string): void {
  setAccessToken(accessToken);
  writeRefreshToken(refreshToken);
  setState({ status: 'authenticated', user: userFromAccessToken(accessToken) });
  scheduleRefresh(accessToken);
}

function clearSession(): void {
  cancelScheduledRefresh();
  setAccessToken(null);
  writeRefreshToken(null);
  setState(SIGNED_OUT);
}

/** Сессия кончилась локально, но чужой refresh в хранилище не трогаем. */
function dropLocalSession(): void {
  cancelScheduledRefresh();
  setAccessToken(null);
  setState(SIGNED_OUT);
}

async function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = navigator.locks;
  if (!locks?.request) {
    return run();
  }
  return locks.request(REFRESH_LOCK, run);
}

async function refreshOnce(): Promise<string | null> {
  const stored = readRefreshToken();

  if (!stored) {
    dropLocalSession();
    return null;
  }

  if (isExpired(stored)) {
    clearSession();
    return null;
  }

  try {
    const pair = await refreshTokens(stored);
    applyTokens(pair.accessToken, pair.refreshToken);
    return pair.accessToken;
  } catch (error) {
    // Сеть недоступна — сессию не рвём, попробуем позже.
    if (error instanceof AuthApiError && error.code === 'UNAVAILABLE') {
      throw error;
    }
    clearSession();
    return null;
  }
}

/** Действующий access-токен; при необходимости обновляет пару. */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const current = getAccessTokenRaw();
  if (current && !isExpired(current)) {
    return current;
  }

  return withRefreshLock(async () => {
    // Пока ждали блокировку, другая вкладка могла всё сделать за нас.
    const afterLock = getAccessTokenRaw();
    if (afterLock && !isExpired(afterLock)) {
      return afterLock;
    }
    return refreshOnce();
  });
}

/** Совместимое имя для вызывающего кода. */
export const getAccessToken = ensureFreshAccessToken;

function attachEnvironmentListeners(): void {
  if (listenersAttached || typeof window === 'undefined') return;
  listenersAttached = true;

  // Вход или выход в другой вкладке.
  window.addEventListener('storage', (event) => {
    if (event.key !== REFRESH_TOKEN_KEY) return;

    if (!event.newValue) {
      dropLocalSession();
      return;
    }

    if (state.status !== 'authenticated') {
      void ensureFreshAccessToken().catch(() => {});
    }
  });

  // Вкладка была в фоне — access мог протухнуть, таймер в фоне ненадёжен.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && state.status === 'authenticated') {
      void ensureFreshAccessToken().catch(() => {});
    }
  });

  window.addEventListener('online', () => {
    if (readRefreshToken()) {
      void ensureFreshAccessToken().catch(() => {});
    }
  });
}

/** Восстановление сессии при старте приложения. */
export function bootstrapSession(): Promise<void> {
  bootstrapPromise ??= (async () => {
    attachEnvironmentListeners();

    if (!readRefreshToken()) {
      setState(SIGNED_OUT);
      return;
    }

    try {
      await ensureFreshAccessToken();
    } catch {
      // Бэкенд недоступен: в кабинет не пускаем, но сохранённый refresh оставляем.
      dropLocalSession();
    }
  })();

  return bootstrapPromise;
}

export function subscribeToSession(onChange: (next: SessionState) => void): () => void {
  listeners.add(onChange);
  onChange(state);
  void bootstrapSession();
  return () => {
    listeners.delete(onChange);
  };
}

export function getSession(): SessionState {
  return state;
}

/** Шаг 1: запросить SMS-код. Возвращает id попытки. */
export function startPhoneSignIn(phoneNumber: string): Promise<string> {
  return requestSmsCode(phoneNumber);
}

/** Шаг 2: подтвердить код и открыть сессию. */
export async function completePhoneSignIn(attemptId: string, smsCode: string): Promise<void> {
  const pair = await confirmSmsCode(attemptId, smsCode);
  applyTokens(pair.accessToken, pair.refreshToken);
}

export async function signOut(): Promise<void> {
  const stored = readRefreshToken();
  clearSession();

  if (!stored) return;

  try {
    await revokeRefreshToken(stored);
  } catch {
    // Локальную сессию уже сбросили; отзыв на сервере не должен блокировать выход.
  }
}
